import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Calendar as CalendarIcon, Phone, Video, Home as HomeIcon, MessageSquare, Plus, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KIND_ICON = { call: Phone, whatsapp: MessageSquare, meeting: Video, site_visit: HomeIcon, other: CalendarIcon };
const KIND_LABEL = { call: "Call", whatsapp: "WhatsApp", meeting: "Meeting", site_visit: "Site visit", other: "Other" };

function bucketOf(fu) {
  if (fu.completed) return "done";
  const now = new Date();
  const due = new Date(fu.due_at);
  const today = now.toISOString().slice(0,10);
  const dueDay = fu.due_at.slice(0,10);
  if (dueDay < today) return "overdue";
  if (dueDay === today) return "today";
  return "upcoming";
}

export default function FollowUps() {
  const [items, setItems] = useState([]);
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ lead_id: "", kind: "call", due_at: "", notes: "" });

  const load = async () => setItems((await api.get("/follow-ups")).data);
  useEffect(() => {
    load();
    api.get("/leads").then(r => setLeads(r.data));
  }, []);

  const save = async () => {
    if (!f.due_at) { toast.error("Pick a date/time"); return; }
    try {
      await api.post("/follow-ups", f);
      toast.success("Follow-up scheduled");
      setOpen(false); setF({ lead_id: "", kind: "call", due_at: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const complete = async (id) => {
    await api.put(`/follow-ups/${id}`, { completed: true, outcome: "Completed" });
    toast.success("Marked complete"); load();
  };
  const del = async (id) => { await api.delete(`/follow-ups/${id}`); load(); };

  const groups = { overdue: [], today: [], upcoming: [], done: [] };
  items.forEach(fu => groups[bucketOf(fu)].push(fu));

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="followups-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Follow-ups</h1>
          <p className="text-sm text-slate-500 mt-1">Never miss a callback, meeting or site visit</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-followup-button"><Plus className="w-4 h-4 mr-1"/>New follow-up</Button></DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle>Schedule follow-up</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Lead</Label>
                <Select value={f.lead_id} onValueChange={(v)=>setF({...f, lead_id: v})}>
                  <SelectTrigger data-testid="fu-lead-select"><SelectValue placeholder="Choose lead"/></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {leads.map(l => <SelectItem key={l.id} value={l.id}>{l.name} · {l.phone} · {l.score}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Type</Label>
                <Select value={f.kind} onValueChange={(v)=>setF({...f, kind: v})}>
                  <SelectTrigger data-testid="fu-kind-select"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.entries(KIND_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Due date &amp; time</Label>
                <Input type="datetime-local" value={f.due_at} onChange={(e)=>setF({...f,due_at:e.target.value ? new Date(e.target.value).toISOString() : ""})} data-testid="fu-due-input"/>
              </div>
              <div><Label>Notes</Label><Input value={f.notes} onChange={(e)=>setF({...f,notes:e.target.value})} placeholder="What to discuss…" data-testid="fu-notes-input"/></div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fu-save-button">Schedule</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {["overdue", "today", "upcoming"].map((b) => (
        <div key={b}>
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-sm font-semibold text-slate-700 capitalize">{b}</h2>
            <Badge className={b==="overdue"?"bg-rose-100 text-rose-800":b==="today"?"bg-emerald-100 text-emerald-800":"bg-indigo-100 text-indigo-800"}>{groups[b].length}</Badge>
          </div>
          {groups[b].length === 0 ? <div className="text-xs text-slate-400 pb-4">Nothing here.</div> :
            <div className="space-y-2">
              {groups[b].map((fu) => {
                const Icon = KIND_ICON[fu.kind] || CalendarIcon;
                return (
                  <Card key={fu.id} className="p-4 border-slate-200 flex items-center gap-4" data-testid={`fu-item-${fu.id}`}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${b==="overdue"?"bg-rose-50 text-rose-700":"bg-emerald-50 text-emerald-700"}`}>
                      <Icon className="w-5 h-5"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">{KIND_LABEL[fu.kind]}</div>
                      <div className="text-xs text-slate-500">{new Date(fu.due_at).toLocaleString()}</div>
                      {fu.notes && <div className="text-xs text-slate-600 mt-1 truncate">{fu.notes}</div>}
                    </div>
                    <Button size="sm" variant="outline" onClick={()=>complete(fu.id)} data-testid={`fu-complete-${fu.id}`}><Check className="w-3 h-3 mr-1"/>Done</Button>
                    <Button size="sm" variant="ghost" onClick={()=>del(fu.id)} data-testid={`fu-delete-${fu.id}`}><Trash2 className="w-3 h-3 text-rose-600"/></Button>
                  </Card>
                );
              })}
            </div>
          }
        </div>
      ))}
    </div>
  );
}
